import React, { useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { useNavigate } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Container, Grid } from "@mui/material";
import logo from "./../../assets/safe_logo.png";
import RoundButton from "../../components/RoundButton";
import forgotPasswordScheme from "../../validations/forgotPasswordScheme";
import { Controller, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import { forgotPasswordRequest } from "../../redux/forgotPassword/action";
import CustomLoader from "../../components/CustomLoader";
import storePersist, { localConstant } from "../../services/localStorage";
import background_image from "./../../assets/png/background_image.png";
import { useResponsive } from "../../hooks/useResponsive";
import { ReactComponent as ArrowLeftSvg } from "./../../assets/svg/arrow-left.svg";
import { PasswordChecklist } from "../signup";
import { useTranslation } from "react-i18next";

const ForgotPasswordPage = () => {
  const { t } = useTranslation();

  const { isLoading } = useSelector((state) => state.forgotReducer);
  const { updatePasswordDetails } = useSelector(
    (state) => state.verifyUserReducer,
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const smDown = useResponsive("down", "sm");

  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const handleClickConfirmShowPassword = () =>
    setShowConfirmPassword(!showConfirmPassword);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    mode: "all",
    resolver: yupResolver(forgotPasswordScheme),
  });

  const formForgotPassword = (values) => {
    const emailId = storePersist.get(localConstant.LOCAL_EMAIL);
    if (updatePasswordDetails) {
      const updatePasswordParams = updatePasswordDetails;
      updatePasswordParams.password = values?.confirmpassword;
      dispatch(
        forgotPasswordRequest(updatePasswordParams, (response) => {
          if (response?.user) {
            navigate("/login");
          }
        }),
      );
    } else {
      dispatch(
        forgotPasswordRequest(
          { email: emailId, password: values?.confirmpassword },
          (response) => {
            if (response?.user) {
              navigate("/login");
            }
          },
        ),
      );
    }
  };

  const password = useWatch({ control, name: "password" });
  return (
    <Container
      disableGutters
      maxWidth={false}
      style={{
        position: "relative",
        minHeight: "100vh",
        backgroundColor: "#80BCFF4F",
        backgroundImage: `url(${background_image})`,
        backgroundPosition: "center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        width: "100vw",
        height: "100%",
      }}
    >
      <Box
        maxWidth="xs"
        sx={{
          flexGrow: 1,
          width: 1,
          minHeight: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            width: 1,
            height: 1,
            display: "flex",
            justifyContent: "center",
            alignContent: "center",
            flexDirection: ["column", "column", "row"],
          }}
        >
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: 1, width: 1, padding: 0, gap: 5 }}
          >
            <img src={logo} alt="Your Alt Text" />
            <Card
              sx={{
                p: 3,
                width: 1,
                height: smDown ? "100vh" : 1,
                maxWidth: 600,
              }}
            >
              <form onSubmit={handleSubmit((data) => formForgotPassword(data))}>
                <Grid container sx={{ mb: 3 }} direction={"row"}>
                  <ArrowLeftSvg
                    style={{ alignSelf: "center" }}
                    onClick={() => {
                      navigate(-1);
                    }}
                  />
                  <Typography
                    sx={{ ml: 1 }}
                    variant="titleText"
                    color="primary.main"
                  >
                    {t("pages.forgotPassword.text08")}
                  </Typography>
                </Grid>
                <Stack spacing={3}>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="New password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        error={!!errors.password}
                        helperText={errors.password?.message}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={handleClickShowPassword}
                                edge="end"
                              >
                                {showPassword ? (
                                  <Visibility />
                                ) : (
                                  <VisibilityOff />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  ></Controller>
                  <Controller
                    name="confirmpassword"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Confirm password"
                        name="newpassword"
                        type={showConfirmPassword ? "text" : "password"}
                        error={!!errors.confirmpassword}
                        helperText={errors.confirmpassword?.message}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={handleClickConfirmShowPassword}
                                edge="end"
                              >
                                {showConfirmPassword ? (
                                  <Visibility />
                                ) : (
                                  <VisibilityOff />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  ></Controller>
                  <PasswordChecklist password={password} />
                </Stack>
                <RoundButton
                  sx={{ mt: 5, mb: 5 }}
                  size="large"
                  fullWidth
                  variant="contained"
                  type="submit"
                  color="primary"
                  rounded
                >
                  {" "}
                  <Typography variant="buttonText" color="common.buttonText">
                    {t("pages.forgotPassword.text09")}
                  </Typography>
                </RoundButton>
              </form>
            </Card>
          </Stack>
        </Box>
      </Box>
      {isLoading && <CustomLoader />}
    </Container>
  );
};

export default ForgotPasswordPage;
